// Globals
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { updateUserFilters, getUsersList, selectUserFilters } from '../../feature';

// Components
import { StyledFilterRow, StyledSelect, StyledInput } from './UsersFiltersContainerStyles';

// Helpers
import { GetUsersListDTO } from '../../dtos';
import { UserFiltersFields } from '../../helpers/types';
import { getStatusOptions } from '../../helpers/getOptions';

type IUserFiltersContainerProps = {
  children?: ReactNode;
  className?: string;
};

const UsersFiltersContainer = ({ children, className }: IUserFiltersContainerProps) => {
  const { t } = useTranslation(['users']);
  const dispatch = useAppDispatch();
  const userFilters = useAppState(selectUserFilters);

  const handleDebounceSearch = useCallback(
    debounce(async (searchParams) => {
      const dto = new GetUsersListDTO(searchParams);
      dispatch(getUsersList(dto));
    }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...userFilters, [name]: value || undefined };

    dispatch(updateUserFilters(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleSelect = (name: string) => (value: unknown) => {
    const newQuery = {
      ...userFilters,
      [name]: value,
    };
    dispatch(updateUserFilters(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <StyledInput
            name={UserFiltersFields.TRIPLETEX_CUSTOMER_NUMBER}
            onChange={handleSearch}
            value={userFilters?.[UserFiltersFields.TRIPLETEX_CUSTOMER_NUMBER] ?? ''}
            allowClear
          />
        </th>
        <th>
          <StyledInput
            name={UserFiltersFields.NAME}
            onChange={handleSearch}
            value={userFilters?.[UserFiltersFields.NAME] ?? ''}
            allowClear
          />
        </th>
        <th>
          <StyledInput
            name={UserFiltersFields.PHONE_NUMBER}
            onChange={handleSearch}
            value={userFilters?.[UserFiltersFields.PHONE_NUMBER] ?? ''}
            allowClear
          />
        </th>
        <th>
          <StyledInput
            name={UserFiltersFields.EMAIL}
            onChange={handleSearch}
            value={userFilters?.[UserFiltersFields.EMAIL] ?? ''}
            allowClear
          />
        </th>
        <th>
          <StyledInput
            name={UserFiltersFields.ADDRESS}
            onChange={handleSearch}
            value={userFilters?.[UserFiltersFields.ADDRESS] ?? ''}
            allowClear
          />
        </th>
        <th />
        <th />
        <th>
          <StyledSelect options={getStatusOptions(t)} onChange={handleSelect(UserFiltersFields.STATUS)} allowClear />
        </th>
        <th />
      </StyledFilterRow>
    </thead>
  );
};

export default memo(UsersFiltersContainer);
